import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import i18n from "i18next";
import mixpanel from "mixpanel-browser";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { initReactI18next } from "react-i18next";
import { BrowserRouter } from "react-router-dom";
import { IntercomProvider } from "react-use-intercom";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";

import RenderIf from "common/components/RenderIf";
import ToastContainer from "common/components/ui/ToastContainer";
import { TooltipProvider } from "common/components/ui/Tooltip";
import { EXTENSION_TUNNEL_ORIGIN } from "common/constants";
import { AuthProvider } from "common/context/AuthContext";
import { HTTPError } from "common/helpers/HTTP";
import registerServiceWorker from "common/helpers/registerServiceWorker";
import {
  getWhitelabelCustomization,
  isProductionEnvironment,
} from "common/helpers/utils";

import App from "./App";

import enLocale from "assets/locales/en.json";
import "assets/global.css";

const { hash } = window.location;

if (hash) {
  const token = hash.substring(1);

  if (token !== "") {
    localStorage.setItem("auth", token);
  }
}

if (isProductionEnvironment()) {
  Sentry.init({
    environment: import.meta.env.MODE,
    dsn: "https://fc3b760b6e7c49a390de101fd5e9b4b3@o769987.ingest.sentry.io/6299134",
    release: import.meta.env.VITE_SENTRY_RELEASE,
    beforeSend(event, hint) {
      // we don't send fetch errors
      if (hint.originalException instanceof HTTPError) {
        return null;
      }
      return event;
    },
  });

  mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN, {
    debug: false,
    track_pageview: "url-with-path-and-query-string",
    persistence: "localStorage",
    record_sessions_percent: 100,
    record_mask_text_selector: "",
  });
}

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    // the translations
    // (tip move them in a JSON file and import them,
    // or even better, manage them via a UI: https://react.i18next.com/guides/multiple-translation-files#manage-your-translations-with-a-management-gui)
    resources: {
      en: enLocale,
    },
    lng: "en", // if you're using a language detector, do not define the lng option
    fallbackLng: "en",
    interpolation: {
      escapeValue: false, // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
    },
  })
  .then(() => {});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

queryClient.setQueryDefaults([{ accountId: null }], { enabled: false });

const container = document.getElementById("react-root");
const root = createRoot(container);

root.render(
  <StrictMode>
    <RenderIf condition={!!getWhitelabelCustomization()}>
      {/** biome-ignore lint/a11y/useIframeTitle: this iframe is not for the user */}
      <iframe
        id="extension-tunnel"
        src={`${EXTENSION_TUNNEL_ORIGIN}/extension-tunnel.html`}
        className="hidden"
      />
    </RenderIf>
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} />
      <IntercomProvider appId={import.meta.env.VITE_INTERCOM_APP_ID}>
        <BrowserRouter>
          <QueryParamProvider adapter={ReactRouter6Adapter}>
            <AuthProvider>
              <TooltipProvider delayDuration={300}>
                <App />
                <ToastContainer />
              </TooltipProvider>
            </AuthProvider>
          </QueryParamProvider>
        </BrowserRouter>
      </IntercomProvider>
    </QueryClientProvider>
  </StrictMode>,
);

registerServiceWorker();
